import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnInit,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { BehaviorSubject, Observable, merge } from 'rxjs';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import {
  ClosedModalResponse,
  FailedModalResponse,
  SuccessModalResponse,
} from '../models';
import { MfaService } from '../services';
import { scan, map } from 'rxjs/operators';

@Component({
  selector: 'lib-mfa-challenge-modal',
  templateUrl: './mfa-challenge-modal.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MfaChallengeModalComponent implements OnInit {
  private readonly _email$ = new BehaviorSubject<boolean>(false);
  private readonly _mobile$ = new BehaviorSubject<boolean>(false);
  private readonly _sentTo$ = new BehaviorSubject<string>('');
  private readonly _showFailedAttempt$ = new BehaviorSubject<boolean>(false);
  private readonly _token$ = new BehaviorSubject<string>('');

  @Input()
  set email(value: boolean) {
    this._email$.next(value);
  }
  get email(): boolean {
    return this._email$.getValue();
  }

  @Input()
  set mobile(value: boolean) {
    this._mobile$.next(value);
  }
  get mobile(): boolean {
    return this._mobile$.getValue();
  }

  @Input()
  set sentTo(value: string) {
    this._sentTo$.next(value);
  }
  get sentTo(): string {
    return this._sentTo$.getValue();
  }

  @Input()
  set token(value: string) {
    this._token$.next(value);
  }
  get token(): string {
    return this._token$.getValue();
  }

  formGroup = this.fb.group({
    mfaCode: [
      '',
      [
        Validators.required,
        Validators.pattern(/[1-9][0-9][0-9][0-9][0-9][0-9]/),
      ],
    ],
  });
  viewModel$!: Observable<ViewModel>;

  constructor(
    private activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private mfaSvc: MfaService,
    private toastr: ToastrService
  ) {}

  private initViewModel() {
    this.viewModel$ = merge(
      this._email$.pipe(map((email) => ({ email }))),
      this._mobile$.pipe(map((mobile) => ({ mobile }))),
      this._sentTo$.pipe(map((sentTo) => ({ sentTo }))),
      this._token$.pipe(map((token) => ({ token }))),
      this._showFailedAttempt$.pipe(
        map((showFailedAttempt) => ({ showFailedAttempt }))
      )
    ).pipe(scan((acc, value) => ({ ...acc, ...value }), {} as ViewModel));
  }

  close() {
    this.activeModal.close({ status: 'closed' } as ClosedModalResponse);
  }

  ngOnInit(): void {
    this.initViewModel();
  }

  resend() {
    this.formGroup.reset();
    this._showFailedAttempt$.next(false);
    this.mfaSvc.createChallenge$().subscribe((vm) => {
      this.token = vm.token;
      this.mobile = vm.mobile;
      this.email = vm.email;
      this.sentTo = vm.sentTo;
    });
  }

  submit() {
    const fv = this.formGroup.value;

    this.mfaSvc
      .evaluateChallenge$(this.token, +fv.mfaCode)
      .subscribe((resp) => {
        if (resp == 'accept') {
          this.toastr.success('MFA verified');
          this.activeModal.close({
            status: 'accepted',
            mfaToken: this.token,
          } as SuccessModalResponse);
        } else if (resp == 'incorrect') {
          this.formGroup.reset();
          this._showFailedAttempt$.next(true);
        } else {
          this.toastr.error('MFA verification failed');
          this.activeModal.close({
            status: 'failed',
          } as FailedModalResponse);
        }
      });
  }
}
interface ViewModel {
  email: boolean;
  mobile: boolean;
  sentTo: string;
  showFailedAttempt: boolean;
  token: string;
}
